/**
 * PlaceSeasonOverlays API – CRUD for place_season_overlays via Supabase client.
 * Respects RLS: public read for public places, write via can_edit_place().
 */

import { supabase } from '@/integrations/supabase/client';
import type { Season } from '@/lib/season';

export interface SeasonOverlay {
  id: string;
  place_id: string;
  season: Season;
  summary_sv: string | null;
  summary_en: string | null;
  hero_media_asset_id: string | null;
  gallery_media_asset_ids: string[];
  did_you_know_sv: string | null;
  did_you_know_en: string | null;
  typical_duration: string | null;
  created_at: string;
  updated_at: string;
}

/** Fetch overlay for a specific place + season. Returns null if none exists. */
export async function getOverlay(
  placeId: string,
  season: Season,
): Promise<SeasonOverlay | null> {
  const { data, error } = await supabase
    .from('place_season_overlays')
    .select('*')
    .eq('place_id', placeId)
    .eq('season', season)
    .maybeSingle();

  if (error) throw error;
  return data as SeasonOverlay | null;
}

/** Fetch all overlays for a place (all seasons). */
export async function getOverlaysForPlace(
  placeId: string,
): Promise<SeasonOverlay[]> {
  const { data, error } = await supabase
    .from('place_season_overlays')
    .select('*')
    .eq('place_id', placeId);

  if (error) throw error;
  return (data ?? []) as SeasonOverlay[];
}

/** Upsert an overlay for a place + season. Uses unique(place_id, season). */
export async function upsertOverlay(
  placeId: string,
  season: Season,
  payload: {
    summary_sv?: string | null;
    summary_en?: string | null;
    hero_media_asset_id?: string | null;
    gallery_media_asset_ids?: string[];
    did_you_know_sv?: string | null;
    did_you_know_en?: string | null;
    typical_duration?: string | null;
  },
): Promise<SeasonOverlay> {
  const { data, error } = await supabase
    .from('place_season_overlays')
    .upsert(
      {
        place_id: placeId,
        season,
        summary_sv: payload.summary_sv ?? null,
        summary_en: payload.summary_en ?? null,
        hero_media_asset_id: payload.hero_media_asset_id ?? null,
        gallery_media_asset_ids: payload.gallery_media_asset_ids ?? [],
        did_you_know_sv: payload.did_you_know_sv ?? null,
        did_you_know_en: payload.did_you_know_en ?? null,
        typical_duration: payload.typical_duration ?? null,
      },
      { onConflict: 'place_id,season' },
    )
    .select('*')
    .single();

  if (error) throw error;
  return data as SeasonOverlay;
}

/** Delete overlay for a place + season. */
export async function deleteOverlay(
  placeId: string,
  season: Season,
): Promise<void> {
  const { error } = await supabase
    .from('place_season_overlays')
    .delete()
    .eq('place_id', placeId)
    .eq('season', season);

  if (error) throw error;
}
